<template>
  <v-data-table
    :headers="tableHeaders"
    :items="milestones"
    v-model="selectedPayments"
    item-key="id"
    hide-default-footer
    :show-select="canUserEdit"
    :style="{ padding: '10px 0 30px' }"
    :items-per-page="-1"
  >
    <template v-slot:footer v-if="Math.round(remainingValue) !== 0 && !payment.multiCurrencyMilestone">
      <v-alert
        dense
        outlined
        :type="alertType"
        :style="{
          margin: '0 25%',
          width: '50%'
        }"
      >
        {{remainingValueText}}
      </v-alert>
    </template>
    <template v-slot:item.date="{ item }">
      {{item.date | formatDate}}
    </template>
    <template v-slot:item.name="{ item }">
      <v-chip small>{{item.name}}</v-chip>
    </template>
    <template v-slot:item.percentOfContractPrice="{ item }">
      <span class="nowrap">{{item.percentOfContractPrice | toFixed}}%</span>
    </template>
    <template v-slot:item.paymentAmountCents="{ item }">
      <PricesDisplay
        :prices="item.paymentAmountCents"
        :contract-currencies="currencies"
        :chosen-currency="chosenCurrency"
        :item-currency="item.currency"
      />
    </template>
    <template v-slot:item.application="{ item }">
      <small class="nowrap d-block">
        <v-icon small :color="item.amountAssignedToApplication .sum? 'blue' : ''">mdi-plus</v-icon>
        {{item.amountAssignedToApplication.sum | formatCurrency(chosenCurrency) }}
      </small>
      <small class="nowrap d-block">
        <v-icon small :color="item.amountPaid.sum ? 'success' : ''">mdi-check</v-icon>
        {{item.amountPaid.sum | formatCurrency(chosenCurrency) }}
      </small>
    </template>
    <template v-slot:item.originalDate="{ item }">
      <span class="nowrap">{{item.originalDate | formatDate}}</span>
    </template>
      <template v-slot:item.part="{ item }">
        <div v-if="item.part">
          {{item.part.name}}
          <small class="d-block text--secondary">{{item.purchaseOrder}}</small>
        </div>
        <div v-else>
          {{ $t(partTranslationKey + '.all') }}
          <small class="d-block text--secondary">{{item.purchaseOrder}}</small>
        </div>
      </template>
    <template v-slot:item.actions="{ item }">
      <v-btn
        icon
        small
        @click="handleEditSubPaymentModal(item)"
        :disabled="!canUserEdit"
      >
        <v-icon>mdi-pencil </v-icon>
      </v-btn>
      <SubmitButton
        icon="mdi-delete"
        small
        @click="$emit('delete-submilestone', item)"
        :disabled="!!payment.amountAssignedToApplication.sum || !canUserEdit || payment.multiCurrencyMilestone"
      />
    </template>
  </v-data-table>
</template>

<script>
import { calculateDisplayDateForMilestone, calculateMaxAmountsForPaymentForm } from '@/services'
import { mapState } from 'vuex'
import { getLocalizationContext } from '@/helpers/translations'

export default {
  name: 'SubMilestonesTable',
  props: {
    value: Array,
    payments: Array,
    payment: Object,
    contractPrice: Object,
    paymentApplications: Array,
    currencies: Array,
    parts: Array,
    chosenCurrency: Object,
    checkNameDuplicate: Function,
    isMultiCurrency: Boolean
  },
  data () {
    return {
      key: 0,
      selectedPayments: this.value,
      tableHeaders: [
        { text: this.$t('common.name'), value: 'name' },
        { text: this.$t('common.description'), value: 'description' },
        {
          text: this.$t(`payment.milestone.${this.payment.variationId ? 'percentOfVariation' : 'percentOfContract'}`),
          value: 'percentOfContractPrice',
          width: 130
        },
        { text: this.$t('payment.milestone.payment'), value: 'paymentAmountCents' },
        { text: this.$t('payment.milestone.inApplication'), value: 'application' },
        { text: this.$t('payment.milestone.forecastedDate'), value: 'date' },
        { text: this.$t('payment.milestone.substantiation'), value: 'comment' },
        { text: this.$tc(getLocalizationContext(this.isMultiCurrency) + '.self', 1), value: 'part' },
        { text: '', value: 'actions', sortable: false, width: 120 }
      ]
    }
  },
  computed: {
    ...mapState({
      canUserEdit: state => state.userPermissionsToContract.canUserEdit
    }),
    alertType () {
      return this.remainingValue > 0 ? 'warning' : 'error'
    },
    remainingValue () {
      const paymentsAmount = this.payments.reduce((acc, curr) => acc + curr.paymentAmountCents, 0)
      return this.payment.paymentAmountCents - paymentsAmount
    },
    remainingValueText () {
      const percent = this.remainingValue > 0
        ? this.$options.filters.toFixed(this.remainingValue / this.payment.paymentAmountCents * 100)
        : this.$options.filters.toFixed((this.remainingValue / this.payment.paymentAmountCents * 100) * -1 + 100)
      return this.remainingValue > 0
        ? this.$t('payment.milestone.partsNotCover', { remainingValue: this.$options.filters.formatCurrency(this.remainingValue), percent })
        : this.$t('payment.milestone.partsCoverMore', { percent })
    },
    milestones () {
      return this.payments.map(payment => {
        const price = this.payment.variationId ? this.payment.paymentAmountCents : this.contractPrice
        return {
          ...payment,
          date: calculateDisplayDateForMilestone(this.paymentApplications, payment),
          percentOfContractPrice: (payment.paymentAmountCents * payment.currency.value) / price.sum * 100
        }
      })
    },
    partTranslationKey () {
      return getLocalizationContext(this.isMultiCurrency)
    }
  },
  methods: {
    handleEditSubPaymentModal (subpayment) {
      const prices = isNaN(this.payment.paymentAmountCents) ? this.payment.paymentAmountCents : { [this.currencies[0].key]: this.payment.paymentAmountCents }
      const { maxAmountsPerCurrency, maxAmountsPerPart } = calculateMaxAmountsForPaymentForm(this.milestones, prices, this.currencies, this.parts)
      this.$eventBus.$emit('OPEN_MODAL', {
        component: 'PaymentMilestoneForm',
        options: { maxWidth: 850 },
        scope: {
          title: this.$t('payment.milestone.editPart'),
          payment: { ...subpayment, purchaseOrder: this.payment.purchaseOrder },
          maxAmountsPerCurrency,
          maxAmountsPerPart,
          contractPrice: this.payment.paymentAmountCents,
          currencies: this.currencies,
          isVariation: !!this.payment.variationId,
          parts: this.parts,
          checkNameDuplicate: (milestone) => this.checkNameDuplicate(milestone, subpayment),
          isMultiCurrency: this.isMultiCurrency,
          parentMilestone: this.payment
        },
        handlers: {
          savePayment: (data) => {
            this.$emit('update-submilestone', data)
          }
        }
      })
    }
  },
  watch: {
    selectedPayments (v) {
      this.$emit('input', v)
    },
    value (v) {
      if (!v) {
        this.selectedPayments = []
      }
    }
  }
}
</script>
